/** Lays out the conversation column and its docking artifact panel; owns the page-local panel state. */

import { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react"
import type { KeyboardEvent, ReactNode } from "react"
import { Alert, Box, Button } from "@mui/material"
import type { SxProps, Theme } from "@mui/material"
import { AI_TEAMMATE_EVENTS, PANEL_SURFACES } from "connected-components/ai-teammate/analytics/events"
import { useAnalytics } from "connected-components/analytics"
import { useAppDispatch, useAppSelector, useAppStore } from "hooks/redux"
import { useAccountAlias, useAreEnabledFeaturesResolved, useIsScheduledWorkflowsEnabled } from "resources/account"
import { collapsePanel, expandPanel, selectPanelCollapsed, selectReportToOpen, setReportToOpen } from "../store/agent-chat-slice"
import { ArtifactPanel } from "./artifact-panel"
import { deliverableOf } from "./artifact-contract"
import { KellyLinkContext, useKellyLink } from "../kelly-link-types"
import { REPORT_PANEL_NAME, reportArtifactOf, ReportPanelBody } from "../scheduled-workflows/report-panel"
import { hasPermanentDrawer, useChatLayout } from "./chat-layout-context"
import { isUnclaimedEscape } from "./escape-key"
import { ArtifactPanelProvider, useArtifactPanel } from "./use-artifact-panel"
import type { LinkedArtifact } from "./use-artifact-panel"
import { AREA_CONTAINER_NAME } from "./artifact-panel"
import { DEFAULT_PANEL_WIDTH_PX } from "../geometry/panel-metrics"

// The artifact panel sizes and places itself off this area (cqi + a named container query), never off the viewport.
// isolation: `container-type` is NOT a stacking context in any engine, so without this every z-index inside the
// area resolves in the HOST's context and can outrank the dock's own chrome.
const AREA_SX = { position: "relative", display: "flex", flex: 1, minHeight: 0, isolation: "isolate", containerType: "inline-size", containerName: AREA_CONTAINER_NAME } satisfies SxProps<Theme>
// tabIndex -1: stable focus fallback when the opening card is gone on close. position:relative: containing block for the composer + disclaimer overlay.
const CHAT_COLUMN_SX = { position: "relative", display: "flex", flexDirection: "column", flex: 1, minHeight: 0, minWidth: 0, outline: "none" } satisfies SxProps<Theme>
// Marks the panel events this region triggers itself; the user's own clicks are tracked in conversation-surface.
const AUTO_COLLAPSED = { ...PANEL_SURFACES.PAGE, auto_collapsed: true }

export function ArtifactPanelRegion({ activeThreadId, newChatSeq, children }: { activeThreadId: string | null, newChatSeq?: number, children: ReactNode }) {
  const chatColumnRef = useRef<HTMLDivElement>(null)
  const areaRef = useRef<HTMLDivElement>(null)
  const layout = useChatLayout()
  // The coordination follows the DRAWER, not the mode and not a width: full screen has one to collapse, a
  // docked dock does not, at any width it can be dragged to.
  const hasDrawer = hasPermanentDrawer(layout)
  const { link, accountValue } = useContext(KellyLinkContext)
  const accountAlias = useAccountAlias()
  const areEnabledFeaturesResolved = useAreEnabledFeaturesResolved()
  const loadedReport = useKellyLink("reports")
  const loadedSharedArtifact = useKellyLink("artifacts")
  useKellyLink("scheduled-workflows")
  const reportValue = loadedReport.status === "found" ? loadedReport.value : null
  const sharedArtifactValue = loadedSharedArtifact.status === "found" ? loadedSharedArtifact.value : null
  const loadedChatArtifact = useKellyLink("chat-artifacts")
  const chatArtifactValue = loadedChatArtifact.status === "found" ? loadedChatArtifact.value : null
  const linkedArtifact = useMemo<LinkedArtifact | null>(() => {
    if (areEnabledFeaturesResolved && accountValue?.toLowerCase() === accountAlias.toLowerCase() && link?.type === "reports" && link.id != null && loadedReport.status !== "not-found" && loadedReport.status !== "error") {
      return { artifact: reportArtifactOf(link.id), name: REPORT_PANEL_NAME, closeLink: { type: "scheduled-workflows", id: reportValue?.workflowId ?? null } }
    }
    if (chatArtifactValue != null) return { artifact: chatArtifactValue.artifact, name: chatArtifactValue.artifact.title ?? chatArtifactValue.artifact.name, ownThreadId: chatArtifactValue.threadId }
    return sharedArtifactValue
  }, [link, accountValue, accountAlias, areEnabledFeaturesResolved, loadedReport.status, reportValue, sharedArtifactValue, chatArtifactValue])
  const { artifact, artifactName, isExpanded, close, toggleExpand, handleEscape, contextValue, openReportArtifact } = useArtifactPanel(activeThreadId, chatColumnRef, newChatSeq, linkedArtifact)
  // The user's committed width, in px. Nothing re-clamps it against the area: the CSS clamp does that at paint
  // time, and a JS re-clamp only ever shrinks — a container that narrows and widens again would strand the loss.
  const [panelWidth, setPanelWidth] = useState(DEFAULT_PANEL_WIDTH_PX)

  // A report keeps the drawer open; any other open artifact — or entering full-screen — needs the room, so it collapses the
  // conversations panel and restores it on close (but only what WE auto-collapsed; a user's own prior collapse
  // is left alone). The two transitions are tracked independently so a re-render that changes neither is a
  // no-op; every close cause (button, Esc, thread switch) restores. Dispatch-in-effect, not setState; the
  // collapse flag is read FRESH off the precisely-typed agentChat slice, never a render closure.
  const dispatch = useAppDispatch()
  const store = useAppStore()
  const analytics = useAnalytics()
  const panelCollapsed = useAppSelector(selectPanelCollapsed)
  const hasArtifact = artifact != null

  useKellyLink("threads")
  const failedLoad = [loadedReport, loadedSharedArtifact, loadedChatArtifact].find((load) => load.status === "error")
  const reportToOpen = useAppSelector(selectReportToOpen)
  const isScheduledWorkflowsEnabled = useIsScheduledWorkflowsEnabled()
  useEffect(() => {
    if (reportToOpen == null) return
    if (isScheduledWorkflowsEnabled) openReportArtifact(reportArtifactOf(reportToOpen), REPORT_PANEL_NAME)
    dispatch(setReportToOpen(null))
  }, [reportToOpen, isScheduledWorkflowsEnabled, openReportArtifact, dispatch])
  const reportDeliverable = artifact == null ? null : deliverableOf(artifact)
  const reportDeliverableId = reportDeliverable?.deliverableId
  const reportFileId = reportDeliverable?.fileId
  const hasArtifactOtherThanReport = hasArtifact && reportDeliverableId == null
  const [reportName, setReportName] = useState<{ deliverableId: string, text: string } | null>(null)
  const reportBody = useMemo(
    () => (reportDeliverableId == null ? undefined : <ReportPanelBody deliverableId={reportDeliverableId} fileId={reportFileId} isExpanded={isExpanded} onClose={close} onName={setReportName} />),
    [reportDeliverableId, reportFileId, isExpanded, close],
  )
  const loadedReportName = reportName?.deliverableId === reportDeliverableId ? reportName?.text : undefined
  const prevHasDrawerRef = useRef(hasDrawer)
  const prevHasArtifactRef = useRef(hasArtifactOtherThanReport)
  const prevIsExpandedRef = useRef(isExpanded)
  const autoCollapsedRef = useRef(false)
  // Any re-expand while our latch is set means someone other than our close path re-docked the panel (the user
  // clicked the rail's Expand). Relinquish ownership so a subsequent MANUAL collapse isn't force-expanded when the
  // artifact closes — we only ever restore a collapse that is still ours. (Our own close-time expand clears the
  // latch first, so this is a no-op there; our auto-collapse sets panelCollapsed=true, so it never trips this.)
  useEffect(() => {
    if (!panelCollapsed) autoCollapsedRef.current = false
  }, [panelCollapsed])
  useEffect(() => {
    // Mirrors advance in every layout: freezing them in a dock would make the next transition read a stale
    // "previous", and the gate below is a disable, not a skip.
    const hadDrawer = prevHasDrawerRef.current
    const hadArtifact = prevHasArtifactRef.current
    const wasExpanded = prevIsExpandedRef.current
    prevHasDrawerRef.current = hasDrawer
    prevHasArtifactRef.current = hasArtifactOtherThanReport
    prevIsExpandedRef.current = isExpanded

    // Minimising to a dock hands back any collapse still ours — panelCollapsed is global and would otherwise
    // strand the drawer collapsed on the next full-screen visit with nothing left to restore it.
    if (hadDrawer && !hasDrawer) {
      if (autoCollapsedRef.current) {
        dispatch(expandPanel())
        analytics.track(AI_TEAMMATE_EVENTS.PANEL_EXPANDED, AUTO_COLLAPSED)
      }
      autoCollapsedRef.current = false
      return
    }
    // A docked dock has no drawer to collapse and must never write the global flag.
    if (!hasDrawer) return

    // Gaining a drawer with a non-report artifact already up is the same need as opening one — neither the artifact nor
    // the expand flag changes, so neither branch below fires and the drawer would seat itself beside the
    // artifact. Full screen composes as the page did.
    if (!hadDrawer && hasArtifactOtherThanReport && !store.getState().agentChat.panelCollapsed) {
      dispatch(collapsePanel())
      analytics.track(AI_TEAMMATE_EVENTS.PANEL_COLLAPSED, AUTO_COLLAPSED)
      autoCollapsedRef.current = true
    }

    // Open ↔ close: open a non-report artifact auto-collapses; close restores only what we auto-collapsed.
    if (hadArtifact !== hasArtifactOtherThanReport) {
      if (hasArtifactOtherThanReport) {
        if (!store.getState().agentChat.panelCollapsed) {
          dispatch(collapsePanel())
          analytics.track(AI_TEAMMATE_EVENTS.PANEL_COLLAPSED, AUTO_COLLAPSED)
          autoCollapsedRef.current = true
        }
      } else {
        if (autoCollapsedRef.current) {
          dispatch(expandPanel())
          analytics.track(AI_TEAMMATE_EVENTS.PANEL_EXPANDED, AUTO_COLLAPSED)
        }
        autoCollapsedRef.current = false
      }
    }

    // Entering full-screen while a non-report artifact is docked also collapses the drawer (matching open); exiting does not restore it.
    if (wasExpanded !== isExpanded && isExpanded && hasArtifactOtherThanReport && !store.getState().agentChat.panelCollapsed) {
      dispatch(collapsePanel())
      analytics.track(AI_TEAMMATE_EVENTS.PANEL_COLLAPSED, AUTO_COLLAPSED)
      autoCollapsedRef.current = true
    }
  }, [hasDrawer, hasArtifactOtherThanReport, isExpanded, dispatch, store, analytics])

  // Tell a dock host an artifact wants room. The cleanup hands it back on the way out too — the surface can be
  // torn down with an artifact open (account switch, a Kelly crash caught by the boundary) and a dock left wider
  // than the user ever asked for would outlive the thing it was widened for.
  const onArtifactPresence = layout.mode === "dock" ? layout.onArtifactPresence : null
  useEffect(() => {
    if (onArtifactPresence == null) return undefined
    onArtifactPresence(hasArtifact)
    return () => onArtifactPresence(false)
  }, [onArtifactPresence, hasArtifact])

  /**
   * Escape closes the artifact from anywhere in the region, not only from inside the panel. The panel is a
   * SIBLING of the conversation column, so an Escape raised at the composer or in the dock's header used to
   * bubble straight past it — in a dock that meant one keystroke closed the whole dock and took the artifact
   * and a half-typed follow-up with it. The panel's own handler still runs first and claims the event; this is
   * the rung for every other origin.
   */
  const onAreaKeyDown = useCallback(
    (event: KeyboardEvent<HTMLDivElement>) => {
      if (!hasArtifact || !isUnclaimedEscape(event)) return
      event.preventDefault()
      handleEscape()
    },
    [hasArtifact, handleEscape],
  )

  return (
    <ArtifactPanelProvider value={contextValue}>
      <Box ref={areaRef} sx={AREA_SX} onKeyDown={onAreaKeyDown}>
        <Box ref={chatColumnRef} tabIndex={-1} sx={CHAT_COLUMN_SX}>
          {failedLoad?.status === "error" && (
            <Alert
              severity="error"
              action={<Button color="inherit" size="small" onClick={failedLoad.retry}>Try again</Button>}
            >
              Couldn&apos;t open this link. Please try again.
            </Alert>
          )}
          {children}
        </Box>
        {artifact != null && (
          <ArtifactPanel
            key={artifact.id}
            artifact={artifact}
            artifactName={loadedReportName ?? artifactName}
            isExpanded={isExpanded}
            onClose={close}
            onToggleExpand={toggleExpand}
            onEscape={handleEscape}
            fallbackFocusRef={chatColumnRef}
            width={panelWidth}
            onWidthChange={setPanelWidth}
            containerRef={areaRef}
            threadId={linkedArtifact?.ownThreadId ?? activeThreadId}
            body={reportBody}
          />
        )}
      </Box>
    </ArtifactPanelProvider>
  )
}
